/**
 * Subscribers — manages the per-club persistent mailing list
 * (Email v2 Phase 1).
 *
 * Distinct from Suppressions (people we WON'T send to). This is the
 * positive list: people who opted in via the join-form, were added
 * manually by the admin, or were bootstrapped from past sends.
 *
 * Phase 1 capabilities (Club + Pro):
 *   - View list (filterable by status: active / deleted / all)
 *   - Search by email or name
 *   - Add one (single email + optional name)
 *   - Add many (bulk paste — server parses + dedups)
 *   - Edit name inline
 *   - Soft-delete (status → 'deleted'; row stays so re-imports are blocked)
 *   - Export CSV
 *
 * Phase 2 (Pro-only) adds: groups column, CSV import, bootstrap wizard,
 * group filter.
 */
import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listSubscribers,
  addSubscriberSingle,
  addSubscribersBulk,
  updateSubscriberName,
  deleteSubscriber,
  exportSubscribersCsv,
  importSubscribersCsv,
  getBootstrapCandidates,
  bootstrapApply,
  listGroups,
  setSubscriberGroups,
  addSubscriberToGroups,
  Subscriber,
  AddSubscriberResult,
  CsvImportResult,
  BootstrapCandidates,
  EmailGroup,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import EmailNav from '../components/EmailNav';
import './EmailSubscribers.css';

type StatusFilter = 'active' | 'deleted' | 'all';

export default function EmailSubscribers() {
  const navigate = useNavigate();
  const { plan } = useAuth();
  const isPro = plan === 'pro';
  const [rows, setRows] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');

  // Phase 2 (Pro): groups for the Groups column + filter.
  const [groups, setGroups] = useState<EmailGroup[]>([]);
  const [groupFilter, setGroupFilter] = useState<string>('');
  const groupNameById: Record<string, string> = {};
  for (const g of groups) groupNameById[g.groupId] = g.name;

  // Add modes
  const [addMode, setAddMode] = useState<'single' | 'bulk' | null>(null);
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [bulkPaste, setBulkPaste] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addResult, setAddResult] = useState<AddSubscriberResult | null>(null);

  // Inline editing
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // CSV export
  const [exporting, setExporting] = useState(false);

  // CSV import (Pro, Phase 2)
  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null);

  // Bootstrap from past sends (Pro, Phase 2)
  const [bootstrapPanelOpen, setBootstrapPanelOpen] = useState(false);
  const [bootstrapCandidates, setBootstrapCandidates] = useState<BootstrapCandidates | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [bootstrapBusy, setBootstrapBusy] = useState(false);
  const [bootstrapGroupId, setBootstrapGroupId] = useState<string>('');
  const [bootstrapApplied, setBootstrapApplied] = useState<{ added: number; skipped: number } | null>(null);

  // Inline group editor modal (Pro, Phase 2 polish)
  const [groupEditFor, setGroupEditFor] = useState<Subscriber | null>(null);
  const [groupEditSelection, setGroupEditSelection] = useState<Set<string>>(new Set());
  const [groupEditBusy, setGroupEditBusy] = useState(false);

  // Bulk select state (Pro, Phase 2 polish)
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkAddGroupOpen, setBulkAddGroupOpen] = useState(false);
  const [bulkAddGroupId, setBulkAddGroupId] = useState<string>('');
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounce search by 200ms — fine for client-side filter
  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, search ? 200 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  // Fetch groups once on mount for Pro clubs. Used for the Groups
  // column and the group filter dropdown.
  useEffect(() => {
    if (!isPro) return;
    listGroups()
      .then(res => setGroups(res.groups))
      .catch(() => {
        // 403 / network — silently no-op; Groups column stays hidden.
      });
  }, [isPro]);

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await listSubscribers({
        status: statusFilter,
        search: search.trim() || undefined,
      });
      setRows(res.subscribers);
    } catch (err: any) {
      const msg = err?.message || 'Failed to load subscribers';
      // 403 → free tier, redirect to upgrade
      if (msg.toLowerCase().includes('club / pro')) {
        setError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddSingle = async (e: FormEvent) => {
    e.preventDefault();
    const email = addEmail.trim().toLowerCase();
    if (!email) return;
    setAddBusy(true);
    setAddResult(null);
    try {
      const result = await addSubscriberSingle(email, addName.trim() || undefined);
      setAddResult(result);
      if (result.added > 0) {
        setAddEmail('');
        setAddName('');
        await load();
      }
    } catch (err: any) {
      setAddResult({
        added: 0, addedEmails: [],
        skippedExisting: 0, skippedExistingEmails: [],
        blockedDeleted: 0, blockedDeletedEmails: [],
        invalid: 0, invalidEmails: [],
      });
      setError(err?.message || 'Failed to add');
    } finally {
      setAddBusy(false);
    }
  };

  const handleAddBulk = async (e: FormEvent) => {
    e.preventDefault();
    const raw = bulkPaste.trim();
    if (!raw) return;
    setAddBusy(true);
    setAddResult(null);
    try {
      const result = await addSubscribersBulk(raw);
      setAddResult(result);
      if (result.added > 0) {
        setBulkPaste('');
        await load();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to add');
    } finally {
      setAddBusy(false);
    }
  };

  const handleSaveName = async (email: string) => {
    try {
      await updateSubscriberName(email, editName);
      setRows(prev => prev.map(r => r.email === email ? { ...r, name: editName } : r));
      setEditingEmail(null);
      setEditName('');
    } catch (err: any) {
      setError(err?.message || 'Failed to update name');
    }
  };

  const handleDelete = async (email: string) => {
    if (!confirm(`Remove ${email} from your subscriber list? They'll stop receiving your emails. The record stays so they can't be silently re-added by a CSV import.`)) {
      return;
    }
    try {
      await deleteSubscriber(email);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const handleImportCsv = async () => {
    if (!csvText.trim()) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const res = await importSubscribersCsv(csvText);
      setImportResult(res);
      setCsvText('');
      await load();
    } catch (err: any) {
      setError(err?.message || 'CSV import failed');
    } finally {
      setImportBusy(false);
    }
  };

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const text = String(loadEvent.target?.result || '');
      setCsvText(text);
    };
    reader.readAsText(file);
  };

  const handleOpenBootstrap = async () => {
    setBootstrapPanelOpen(true);
    setBootstrapApplied(null);
    setBootstrapLoading(true);
    try {
      const res = await getBootstrapCandidates();
      setBootstrapCandidates(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to load bootstrap candidates');
    } finally {
      setBootstrapLoading(false);
    }
  };

  const handleBootstrapApply = async () => {
    if (!bootstrapCandidates) return;
    setBootstrapBusy(true);
    try {
      const res = await bootstrapApply(
        bootstrapCandidates.candidates,
        bootstrapGroupId || undefined,
      );
      setBootstrapApplied({ added: res.added, skipped: res.skipped });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Bootstrap import failed');
    } finally {
      setBootstrapBusy(false);
    }
  };

  const openGroupEditor = (sub: Subscriber) => {
    setGroupEditFor(sub);
    setGroupEditSelection(new Set(sub.groups || []));
  };

  const toggleEditGroup = (groupId: string) => {
    setGroupEditSelection(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const saveGroupEdit = async () => {
    if (!groupEditFor) return;
    setGroupEditBusy(true);
    try {
      const newGroups = Array.from(groupEditSelection);
      await setSubscriberGroups(groupEditFor.email, newGroups);
      setRows(prev => prev.map(r =>
        r.email === groupEditFor.email ? { ...r, groups: newGroups } : r,
      ));
      setGroupEditFor(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to save groups');
    } finally {
      setGroupEditBusy(false);
    }
  };

  const toggleBulkSelect = (email: string) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const clearBulkSelection = () => {
    setBulkSelected(new Set());
    setBulkAddGroupOpen(false);
    setBulkAddGroupId('');
  };

  const handleBulkAddToGroup = async () => {
    if (!bulkAddGroupId || bulkSelected.size === 0) return;
    setBulkBusy(true);
    try {
      // Sequential to keep error reporting clean. Lists are small.
      for (const email of bulkSelected) {
        await addSubscriberToGroups(email, [bulkAddGroupId]);
      }
      await load();
      clearBulkSelection();
    } catch (err: any) {
      setError(err?.message || 'Bulk add failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    if (!confirm(`Remove ${bulkSelected.size} subscribers from your list? They'll be soft-deleted.`)) return;
    setBulkBusy(true);
    try {
      for (const email of bulkSelected) {
        await deleteSubscriber(email);
      }
      await load();
      clearBulkSelection();
    } catch (err: any) {
      setError(err?.message || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportSubscribersCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `subscribers-${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="email-page-shell">
      <EmailNav />

      <div className="page-head">
        <div>
          <h1>Subscribers</h1>
          <p className="subtitle">
            Your persistent mailing list. Add members, edit names + groups,
            import from a CSV, or export your list.
          </p>
        </div>
        <div className="head-actions">
          {isPro && (
            <>
              <button
                className="ebtn ebtn-secondary"
                onClick={handleOpenBootstrap}
              >
                Import from past sends
              </button>
              <button
                className="ebtn ebtn-secondary"
                onClick={() => { setImportPanelOpen(true); setImportResult(null); }}
              >
                Import CSV
              </button>
            </>
          )}
          <button
            className="ebtn ebtn-secondary"
            onClick={handleExport}
            disabled={exporting || rows.length === 0}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button className="ebtn ebtn-primary" onClick={() => { setAddMode('single'); setAddResult(null); }}>
            + Add subscriber
          </button>
        </div>
      </div>

      {error && (
        <div className="ealert ealert-error">
          {error}
          {error.toLowerCase().includes('club / pro') && (
            <>
              {' '}
              <a href="/settings#subscription" onClick={(e) => { e.preventDefault(); navigate('/settings'); }}>
                Upgrade your plan →
              </a>
            </>
          )}
        </div>
      )}

      {/* ── Bulk action bar (Pro) ─────────────── */}
      {bulkSelected.size > 0 && (
        <div className="email-bulk-bar">
          <span className="email-bulk-bar-count">
            {bulkSelected.size} selected
          </span>
          <div className="email-bulk-bar-actions">
            {isPro && groups.filter(g => !g.isAuto).length > 0 && (
              bulkAddGroupOpen ? (
                <>
                  <select
                    value={bulkAddGroupId}
                    onChange={e => setBulkAddGroupId(e.target.value)}
                    disabled={bulkBusy}
                    className="bulk-group-select"
                    autoFocus
                  >
                    <option value="">— Pick group —</option>
                    {groups.filter(g => !g.isAuto).map(g => (
                      <option key={g.groupId} value={g.groupId}>{g.name}</option>
                    ))}
                  </select>
                  <button
                    className="ebtn"
                    onClick={handleBulkAddToGroup}
                    disabled={bulkBusy || !bulkAddGroupId}
                  >
                    {bulkBusy ? 'Adding…' : 'Add'}
                  </button>
                  <button
                    className="ebtn"
                    onClick={() => { setBulkAddGroupOpen(false); setBulkAddGroupId(''); }}
                    disabled={bulkBusy}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  className="ebtn"
                  onClick={() => setBulkAddGroupOpen(true)}
                  disabled={bulkBusy}
                >
                  Add to group…
                </button>
              )
            )}
            <button
              className="ebtn ebtn-danger"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
            >
              {bulkBusy ? 'Removing…' : 'Remove from list'}
            </button>
          </div>
          <button
            className="email-bulk-bar-close"
            onClick={clearBulkSelection}
            aria-label="Clear selection"
          >×</button>
        </div>
      )}

      {/* ── CSV Import panel (Pro) ─────────────── */}
      {importPanelOpen && (
        <div className="add-panel">
          <div className="add-panel-tabs">
            <span className="tab active">Import CSV</span>
            <button
              className="add-panel-close"
              onClick={() => { setImportPanelOpen(false); setImportResult(null); setCsvText(''); }}
              aria-label="Close"
            >×</button>
          </div>
          {!importResult ? (
            <>
              <p className="add-csv-hint">
                Paste or upload a CSV with columns: <code>email</code> (required),
                <code> name</code> (optional), <code> groups</code> (optional,
                comma-separated group names). Missing groups will be created
                automatically.
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsvFileChange}
                disabled={importBusy}
                className="add-csv-file"
              />
              <textarea
                placeholder="email,name,groups&#10;parent1@example.com,Jane Doe,Parents,U12 parents&#10;coach1@example.com,,Coaches"
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                rows={10}
                disabled={importBusy}
                className="add-csv-textarea"
              />
              <div className="add-form-row">
                <button
                  className="btn btn-primary"
                  onClick={handleImportCsv}
                  disabled={importBusy || !csvText.trim()}
                >
                  {importBusy ? 'Importing…' : 'Import'}
                </button>
                <span className="hint">
                  Existing active subscribers will have their groups merged.
                  Previously-unsubscribed addresses will be blocked.
                </span>
              </div>
            </>
          ) : (
            <div className="add-result">
              <p className="add-result-success">✓ Added {importResult.added} new</p>
              {importResult.updatedExisting > 0 && (
                <p className="add-result-info">
                  Updated groups on {importResult.updatedExisting} existing subscribers
                </p>
              )}
              {importResult.createdGroups.length > 0 && (
                <p className="add-result-info">
                  Auto-created {importResult.createdGroups.length} groups:{' '}
                  {importResult.createdGroups.map(g => g.name).join(', ')}
                </p>
              )}
              {importResult.blockedDeleted > 0 && (
                <p className="add-result-warning">
                  Blocked {importResult.blockedDeleted} previously-unsubscribed
                </p>
              )}
              {importResult.invalid > 0 && (
                <p className="add-result-warning">
                  Ignored {importResult.invalid} invalid addresses
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Bootstrap-from-past-sends panel (Pro) ─ */}
      {bootstrapPanelOpen && (
        <div className="add-panel">
          <div className="add-panel-tabs">
            <span className="tab active">Import from past sends</span>
            <button
              className="add-panel-close"
              onClick={() => { setBootstrapPanelOpen(false); setBootstrapApplied(null); }}
              aria-label="Close"
            >×</button>
          </div>
          {bootstrapLoading ? (
            <div className="loading-state" style={{ padding: 24 }}>
              <div className="spinner" />
              <p>Reading your send history…</p>
            </div>
          ) : bootstrapApplied ? (
            <div className="add-result">
              <p className="add-result-success">
                ✓ Added {bootstrapApplied.added} to your subscriber list
              </p>
              {bootstrapApplied.skipped > 0 && (
                <p className="add-result-info">
                  Skipped {bootstrapApplied.skipped} (already subscribed or invalid)
                </p>
              )}
            </div>
          ) : bootstrapCandidates && bootstrapCandidates.count === 0 ? (
            <p className="add-csv-hint">
              Your previous send history doesn't have any addresses that aren't
              already on your subscriber list. Nothing to import.
            </p>
          ) : bootstrapCandidates ? (
            <>
              <p className="add-csv-hint">
                Found <strong>{bootstrapCandidates.count}</strong> unique
                addresses from your previous sends that aren't on your
                subscriber list yet.
                {bootstrapCandidates.alreadyOnList > 0 && (
                  <> ({bootstrapCandidates.alreadyOnList} are already on
                  the list and will be skipped.)</>
                )}
              </p>
              {groups.filter(g => !g.isAuto).length > 0 && (
                <div className="bootstrap-group-row">
                  <label>Optionally assign all to a group:</label>
                  <select
                    value={bootstrapGroupId}
                    onChange={e => setBootstrapGroupId(e.target.value)}
                    disabled={bootstrapBusy}
                    className="group-filter"
                  >
                    <option value="">— No group —</option>
                    {groups
                      .filter(g => !g.isAuto)
                      .map(g => (
                        <option key={g.groupId} value={g.groupId}>{g.name}</option>
                      ))}
                  </select>
                </div>
              )}
              <div className="add-form-row">
                <button
                  className="btn btn-primary"
                  onClick={handleBootstrapApply}
                  disabled={bootstrapBusy}
                >
                  {bootstrapBusy ? 'Adding…' : `Add all ${bootstrapCandidates.count} to my list`}
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ── Add panel ────────────────────────── */}
      {addMode && (
        <div className="add-panel">
          <div className="add-panel-tabs">
            <button
              className={addMode === 'single' ? 'tab active' : 'tab'}
              onClick={() => { setAddMode('single'); setAddResult(null); }}
            >
              Single
            </button>
            <button
              className={addMode === 'bulk' ? 'tab active' : 'tab'}
              onClick={() => { setAddMode('bulk'); setAddResult(null); }}
            >
              Bulk paste
            </button>
            <button
              className="add-panel-close"
              onClick={() => { setAddMode(null); setAddResult(null); }}
              aria-label="Close add panel"
            >×</button>
          </div>

          {addMode === 'single' && (
            <form onSubmit={handleAddSingle} className="add-form">
              <div className="add-form-row">
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={addEmail}
                  onChange={e => setAddEmail(e.target.value)}
                  required
                  disabled={addBusy}
                />
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                  disabled={addBusy}
                />
                <button type="submit" className="btn btn-primary" disabled={addBusy || !addEmail.trim()}>
                  {addBusy ? 'Adding…' : 'Add'}
                </button>
              </div>
            </form>
          )}

          {addMode === 'bulk' && (
            <form onSubmit={handleAddBulk} className="add-form">
              <textarea
                placeholder="Paste emails — one per line, or comma-separated&#10;e.g. parent@example.com, coach@example.com"
                value={bulkPaste}
                onChange={e => setBulkPaste(e.target.value)}
                rows={8}
                disabled={addBusy}
              />
              <div className="add-form-row">
                <button type="submit" className="btn btn-primary" disabled={addBusy || !bulkPaste.trim()}>
                  {addBusy ? 'Adding…' : 'Add all'}
                </button>
                <span className="hint">
                  Up to 500 at once. Existing active subscribers are skipped; previously-deleted ones are blocked.
                </span>
              </div>
            </form>
          )}

          {addResult && (
            <div className="add-result">
              {addResult.added > 0 && (
                <p className="add-result-success">✓ Added {addResult.added}</p>
              )}
              {addResult.skippedExisting > 0 && (
                <p className="add-result-info">
                  Skipped {addResult.skippedExisting} already on the list
                </p>
              )}
              {addResult.blockedDeleted > 0 && (
                <p className="add-result-warning">
                  Blocked {addResult.blockedDeleted} previously-unsubscribed
                  {addResult.blockedDeletedEmails.length > 0 && (
                    <> ({addResult.blockedDeletedEmails.slice(0, 3).join(', ')}
                      {addResult.blockedDeletedEmails.length > 3 && '…'})</>
                  )}
                </p>
              )}
              {addResult.invalid > 0 && (
                <p className="add-result-warning">
                  Ignored {addResult.invalid} invalid
                  {addResult.invalidEmails.length > 0 && (
                    <> ({addResult.invalidEmails.slice(0, 3).join(', ')}
                      {addResult.invalidEmails.length > 3 && '…'})</>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Filters + search ────────────────── */}
      <div className="list-filters">
        <div className="status-filter">
          <button
            className={statusFilter === 'active' ? 'pill active' : 'pill'}
            onClick={() => setStatusFilter('active')}
          >
            Active
          </button>
          <button
            className={statusFilter === 'deleted' ? 'pill active' : 'pill'}
            onClick={() => setStatusFilter('deleted')}
          >
            Removed
          </button>
          <button
            className={statusFilter === 'all' ? 'pill active' : 'pill'}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
        </div>
        {isPro && groups.length > 0 && (
          <select
            className="group-filter"
            value={groupFilter}
            onChange={e => setGroupFilter(e.target.value)}
            aria-label="Filter by group"
          >
            <option value="">All groups</option>
            {groups
              .filter(g => !g.isAuto)
              .map(g => (
                <option key={g.groupId} value={g.groupId}>
                  {g.name} ({g.memberCount})
                </option>
              ))}
          </select>
        )}
        <input
          type="search"
          placeholder="Search email or name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {/* ── Table ───────────────────────────── */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading subscribers…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          {search ? (
            <>
              <h3>No matches for "{search}"</h3>
              <p>Try a different search term, or clear the search to see all subscribers.</p>
            </>
          ) : statusFilter === 'active' ? (
            <>
              <h3>No subscribers yet</h3>
              <p>
                Add one or paste a list to get started. New members who fill in the
                join-form will be added automatically once you enable that option there.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setAddMode('single')}
              >
                Add your first subscriber
              </button>
            </>
          ) : (
            <p>No subscribers with this status.</p>
          )}
        </div>
      ) : (
        <div className="card-surface subscribers-table-wrap">
          <table className="subscribers-table">
            <thead>
              <tr>
                {isPro && (
                  <th className="cell-select">
                    <input
                      type="checkbox"
                      checked={
                        rows.length > 0 &&
                        rows.filter(r => !groupFilter || (r.groups || []).includes(groupFilter)).every(r => bulkSelected.has(r.email))
                      }
                      onChange={e => {
                        const visible = rows.filter(r => !groupFilter || (r.groups || []).includes(groupFilter));
                        if (e.target.checked) {
                          setBulkSelected(new Set(visible.map(r => r.email)));
                        } else {
                          clearBulkSelection();
                        }
                      }}
                      aria-label="Select all visible"
                    />
                  </th>
                )}
                <th>Email</th>
                <th>Name</th>
                {isPro && <th>Groups</th>}
                <th>Added</th>
                <th>Source</th>
                {statusFilter !== 'active' && <th>Status</th>}
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows
                .filter(s => !groupFilter || (s.groups || []).includes(groupFilter))
                .map(s => (
                <tr key={s.email} className={s.status === 'deleted' ? 'row-deleted' : ''}>
                  {isPro && (
                    <td className="cell-select">
                      <input
                        type="checkbox"
                        checked={bulkSelected.has(s.email)}
                        onChange={() => toggleBulkSelect(s.email)}
                        aria-label={`Select ${s.email}`}
                      />
                    </td>
                  )}
                  <td className="cell-email">{s.email}</td>
                  <td className="cell-name">
                    {editingEmail === s.email ? (
                      <div className="inline-edit">
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          autoFocus
                          onBlur={() => handleSaveName(s.email)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveName(s.email);
                            if (e.key === 'Escape') { setEditingEmail(null); setEditName(''); }
                          }}
                        />
                      </div>
                    ) : (
                      <span
                        className="editable"
                        onClick={() => { setEditingEmail(s.email); setEditName(s.name); }}
                      >
                        {s.name || <em className="muted">(no name)</em>}
                      </span>
                    )}
                  </td>
                  {isPro && (
                    <td className="cell-groups">
                      <button
                        type="button"
                        className="groups-cell-button"
                        onClick={() => openGroupEditor(s)}
                        title="Click to edit groups"
                      >
                        {(s.groups || []).length === 0 ? (
                          <span className="add-groups-cta">+ Add to group</span>
                        ) : (
                          <div className="subscriber-group-chips">
                            {(s.groups || [])
                              .map(gid => groupNameById[gid])
                              .filter(Boolean)
                              .map((name, i) => (
                                <span key={i} className="subscriber-group-chip">{name}</span>
                              ))}
                          </div>
                        )}
                      </button>
                    </td>
                  )}
                  <td className="cell-date" title={s.addedAt}>
                    {s.addedAt ? new Date(s.addedAt).toLocaleDateString() : ''}
                  </td>
                  <td className="cell-source">
                    <span className={`source-badge source-${s.source}`}>{s.source}</span>
                  </td>
                  {statusFilter !== 'active' && (
                    <td className="cell-status">
                      <span className={`status-badge status-${s.status}`}>
                        {s.status === 'deleted' ? 'Removed' : 'Active'}
                      </span>
                    </td>
                  )}
                  <td className="cell-actions">
                    {s.status === 'active' && (
                      <button
                        className="btn-icon"
                        onClick={() => handleDelete(s.email)}
                        title="Remove from list"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="list-count-footer">
            Showing {rows.length} {rows.length === 1 ? 'subscriber' : 'subscribers'}
          </p>
        </div>
      )}

      {/* ── Inline group editor modal (Pro) ────────── */}
      {groupEditFor && (
        <div className="email-modal-backdrop" onClick={() => !groupEditBusy && setGroupEditFor(null)}>
          <div className="email-modal" onClick={e => e.stopPropagation()}>
            <div className="email-modal-head">
              <h3>Edit groups for {groupEditFor.email}</h3>
              <p className="modal-sub">
                Tick the groups this subscriber should be in.
                "All members" is automatic and includes everyone.
              </p>
            </div>
            <div className="email-modal-body">
              {groups.filter(g => !g.isAuto).length === 0 ? (
                <p className="modal-empty-msg">
                  You haven't created any groups yet.{' '}
                  <a href="/email/groups" onClick={e => { e.preventDefault(); navigate('/email/groups'); }}>
                    Create one →
                  </a>
                </p>
              ) : (
                <ul className="group-toggle-list">
                  {groups.filter(g => !g.isAuto).map(g => {
                    const checked = groupEditSelection.has(g.groupId);
                    return (
                      <li key={g.groupId}>
                        <label className="group-toggle-row">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEditGroup(g.groupId)}
                            disabled={groupEditBusy}
                          />
                          <span className="group-toggle-name">{g.name}</span>
                          <span className="group-toggle-count">{g.memberCount}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="email-modal-foot">
              <button
                className="ebtn ebtn-ghost"
                onClick={() => setGroupEditFor(null)}
                disabled={groupEditBusy}
              >
                Cancel
              </button>
              <button
                className="ebtn ebtn-primary"
                onClick={saveGroupEdit}
                disabled={groupEditBusy}
              >
                {groupEditBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
